import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '../ui/button';
import { Globe, Lock, Wifi, Server, Container, Cpu, Smartphone, Radio, ArrowRight, CheckCircle, Briefcase, Building2, Network, ShieldCheck } from 'lucide-react';
import { Reveal, SectionTitle, Orbs } from './primitives';

export const SCAN_TILES = [
  { id: 'web', label: 'Website Vulnerability Scanner', desc: 'OWASP Top 10, XSS, SQLi, misconfigurations', icon: Globe, hue: 'from-cyan-400/25 to-indigo-500/10' },
  { id: 'api', label: 'API Security Scanner', desc: 'BOLA, broken auth, excessive data exposure', icon: Lock, hue: 'from-indigo-400/25 to-fuchsia-500/10' },
  { id: 'network', label: 'Network & Port Scanner', desc: 'Open ports, exposed services, weak ciphers', icon: Wifi, hue: 'from-fuchsia-400/25 to-orange-500/10' },
  { id: 'infrastructure', label: 'Cloud Infrastructure Audit', desc: 'IAM, storage exposure, logging gaps', icon: Server, hue: 'from-orange-400/25 to-yellow-500/10' },
  { id: 'container', label: 'Container Image Scan', desc: 'CVEs in layers, secrets, privilege escalation', icon: Container, hue: 'from-emerald-400/25 to-cyan-500/10' },
  { id: 'kubernetes', label: 'Kubernetes Posture', desc: 'RBAC, pod security, network policies', icon: Cpu, hue: 'from-sky-400/25 to-indigo-500/10' },
  { id: 'mobile', label: 'Mobile App Scanner', desc: 'Insecure storage, weak crypto, tampering', icon: Smartphone, hue: 'from-violet-400/25 to-pink-500/10' },
  { id: 'iot', label: 'IoT / Firmware Scanner', desc: 'Default creds, unencrypted comms, updates', icon: Radio, hue: 'from-pink-400/25 to-rose-500/10' },
];

export const TrustedBy = () => {
  const items = ['OWASP Top 10', 'NIST CSF 2.0', 'ISO 27001', 'PCI DSS 4.0', 'SOC 2', 'CIS Controls v8', 'MITRE ATT&CK', 'GDPR', 'HIPAA', 'CWE Top 25'];
  return (
    <section className="border-y bg-muted/30 py-6 overflow-hidden" data-testid="landing-trusted">
      <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Findings mapped to the frameworks your auditors expect</p>
      <div className="flex w-max animate-marquee gap-10 px-5">
        {[...items, ...items].map((t, i) => <span key={i} className="text-sm font-heading font-semibold text-muted-foreground/80 whitespace-nowrap flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary/70" />{t}</span>)}
      </div>
    </section>
  );
};

export const ToolsGrid = ({ onPick, active }) => (
  <section className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28" id="tools" data-testid="landing-tools">
    <SectionTitle eyebrow="Free online scanners" title="Eight scanners. One click. Zero setup." sub="Pick a scanner, paste a target, get prioritised findings with remediation guidance — no account required for your first 5 scans." />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
      {SCAN_TILES.map((t, i) => (
        <Reveal key={t.id} delay={i}>
          <button onClick={() => onPick(t.id)} className={`group w-full text-left p-5 rounded-2xl border bg-card lift h-full ${active === t.id ? 'border-primary ring-2 ring-primary/30' : ''}`} data-testid={`tool-tile-${t.id}`}>
            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${t.hue} mb-4`}><t.icon className="h-5 w-5 text-primary" /></div>
            <p className="font-heading font-semibold leading-snug">{t.label}</p>
            <p className="text-sm text-muted-foreground mt-1.5">{t.desc}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200">Scan now <ArrowRight className="h-3.5 w-3.5" /></span>
          </button>
        </Reveal>
      ))}
    </div>
  </section>
);

const FEATURES = [
  { img: '/illustrations/recon.jpg', eyebrow: 'Attack surface mapping', title: 'See what attackers see — before they do', points: ['Crawl & spider every reachable path, parameter and API route', 'Fingerprint technologies, exposed services and hidden files', 'Machine-assisted discovery that scales from one app to thousands'] },
  { img: '/illustrations/scanning.jpg', eyebrow: 'Multi-phase vulnerability scanning', title: 'Confirmed findings, not noisy guesses', points: ['Crawl → Passive → Active → Analyze → Validate → Report pipeline', 'False-positive validation with confidence scoring', 'CWE, OWASP and CVSS classification on every issue'], flip: true },
  { img: '/illustrations/report.jpg', eyebrow: 'Reports that speak for themselves', title: 'Evidence your auditor accepts', points: ['7-section findings: summary, detail, background, remediation, references, classification, request/response', 'AI-generated remediation code and executive summaries', 'Export to PDF, JSON and CSV — or share a live link'] },
  { img: '/illustrations/org.jpg', eyebrow: 'Organizational security', title: 'From code to boardroom controls', points: ['Entra ID posture — live tenant assessment via Microsoft Graph', 'HLD / LLD architecture reviews powered by AI', 'NIST CSF, CIS v8 and ISO 27001 assurance scoring'], flip: true },
];

export const Features = () => (
  <section className="relative py-20 md:py-28 overflow-hidden" data-testid="landing-features">
    <Orbs variant="section" />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8">
      <SectionTitle eyebrow="What you can do with Vaptara" title="Built for actual security testing, not just detection." sub="Coverage, consolidation and automation for security teams — with the depth and control professional pentesters count on." />
      <div className="mt-16 space-y-20">
        {FEATURES.map((f, i) => (
          <div key={f.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${f.flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">{f.eyebrow}</p>
              <h3 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{f.title}</h3>
              <ul className="mt-6 space-y-3">{f.points.map(p => <li key={p} className="flex gap-3 text-muted-foreground"><CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>{p}</span></li>)}</ul>
            </Reveal>
            <Reveal delay={1}>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-indigo-500/10 bg-[#070B1A]">
                <img src={f.img} alt={f.eyebrow} className="w-full aspect-square object-cover max-h-[80vh]" loading="lazy" />
              </div>
            </Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PERSONAS = [
  { icon: Briefcase, title: 'Security consultants', tag: 'Work faster, prove more', points: ['Launch web & network scans for new engagements instantly', 'Client-ready reports with reusable findings', 'Track products, engagements and retests in one place'] },
  { icon: Building2, title: 'Internal security teams', tag: 'Maintain visibility, reduce risk', points: ['Continuous coverage across all your products', 'Org-level controls: Entra ID, architecture reviews, assurance', 'Dark web exposure & threat-intel matched to your stack'] },
  { icon: Network, title: 'Managed security providers', tag: 'Deliver more with less overhead', points: ['Multi-product workspaces and engagement tracking', 'Credit-based scaling that matches client demand', 'Exports and shareable links for hand-off'] },
];

export const Personas = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28" data-testid="landing-personas">
      <SectionTitle eyebrow="Vaptara is for" title="One platform, three ways to win." align="center" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        {PERSONAS.map((p, i) => (
          <Reveal key={p.title} delay={i}>
            <div className="beam-border rounded-3xl p-7 bg-card h-full flex flex-col">
              <div className="inline-flex p-3 rounded-2xl bg-primary/10 w-fit"><p.icon className="h-6 w-6 text-primary" /></div>
              <h3 className="font-heading font-bold text-xl mt-5">{p.title}</h3>
              <p className="text-sm text-primary font-medium">{p.tag}</p>
              <ul className="mt-5 space-y-2.5 text-sm text-muted-foreground flex-1">{p.points.map(x => <li key={x} className="flex gap-2"><CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />{x}</li>)}</ul>
              <Button variant="outline" className="mt-6 rounded-full w-fit" onClick={() => navigate('/plans')} data-testid={`persona-cta-${i}`}>Start now <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const CountUp = ({ to, suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - start) / 1400); setV(Math.round(to * (1 - Math.pow(1 - p, 3)))); if (p < 1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }, [inView, to]);
  return <span ref={ref}>{v.toLocaleString()}{suffix}</span>;
};

export const Stats = () => (
  <section className="relative overflow-hidden py-20" data-testid="landing-stats">
    <div className="absolute inset-0 bg-gradient-accent opacity-[0.07]" />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {[[8, '', 'Scanner engines'], [6, '', 'Pipeline phases per scan'], [1700, '+', 'Actively exploited CVEs tracked (CISA KEV)'], [3, '', 'Assurance frameworks scored']].map(([n, s, l], i) => (
        <Reveal key={l} delay={i} className="text-center lg:text-left">
          <p className="text-4xl sm:text-5xl font-heading font-extrabold text-gradient"><CountUp to={n} suffix={s} /></p>
          <p className="text-sm text-muted-foreground mt-2">{l}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Footer = () => {
  const navigate = useNavigate();
  return (
    <footer className="relative border-t bg-[#070B1A] text-white overflow-hidden" data-testid="landing-footer">
      <div className="absolute inset-0 noise" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <p className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-gradient">Vaptara</p>
          <p className="text-sm text-white/60 mt-3 max-w-md">AI-driven vulnerability management, from free online scanners to organisational security assurance.</p>
        </div>
        {[['Product', [['Free scanners', '/free-scan'], ['Pricing', '/plans'], ['Sign in', '/login']]], ['Platform', [['Vulnerability scanner', '/login?next=/scanner'], ['Organizational security', '/login?next=/org-security'], ['Security center', '/login?next=/security-center']]]].map(([h, links]) => (
          <div key={h}><p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">{h}</p><ul className="space-y-2">{links.map(([l, to]) => <li key={l}><button onClick={() => navigate(to)} className="text-sm text-white/80 hover:text-white transition-colors">{l}</button></li>)}</ul></div>
        ))}
      </div>
      <div className="relative border-t border-white/10 py-5 text-center text-xs text-white/40">© {new Date().getFullYear()} Vaptara AI · Findings are generated by an automated engine and should be validated before remediation.</div>
    </footer>
  );
};
